import { useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { useAccountChange } from "../lib/account-context.js";

const GRANT_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{7,127}$/;

export function ecomosHandoffChangeInput(grantId: string) {
  return {
    path: "/integrations/ecomos/handoff" as const,
    body: { grantId },
    destination: "/dashboard",
    failureDestination: "/integrations/ecomos/unavailable",
  };
}

export function EcomosHandoffUnavailablePage(): React.ReactElement {
  return (
    <div style={{ maxWidth: 560, margin: "80px auto", padding: 24 }} role="alert">
      <h1 style={{ fontSize: 24 }}>This connection is unavailable</h1>
      <p style={{ color: "var(--body)" }}>
        Open the Data Dashboard normally and choose the account from the account menu.
      </p>
      <Link to="/dashboard">Go to the dashboard</Link>
    </div>
  );
}

/** Signed-in transition page. The GET itself changes nothing; this page uses
 * the normal fenced POST switch, then hard-navigates only after it commits. */
export default function EcomosHandoffPage(): React.ReactElement {
  const { grantId = "" } = useParams();
  const changeAccount = useAccountChange();
  const started = useRef(false);
  const valid = GRANT_PATTERN.test(grantId);

  useEffect(() => {
    if (!valid || started.current) return;
    started.current = true;
    void changeAccount(ecomosHandoffChangeInput(grantId)).catch(() => {
      // changeAccount has already frozen the old account and scheduled a hard
      // navigation to the explicit unavailable page. Absorb the rejected
      // promise so that expected access failures do not become unhandled ones.
    });
  }, [changeAccount, grantId, valid]);

  if (!valid) {
    return <EcomosHandoffUnavailablePage />;
  }

  return (
    <div style={{ maxWidth: 560, margin: "80px auto", padding: 24 }} role="status">
      <h1 style={{ fontSize: 24 }}>Opening the right dashboard…</h1>
      <p style={{ color: "var(--body)" }}>Checking your access and switching accounts safely.</p>
    </div>
  );
}
